import React, { useMemo, useState } from "react";
import { auth } from "../../../../../../src/backend/firebaseConfig";
import { ChargesProvider, useChargesContext } from "../context/ChargesContext";
import { useCharges } from "../hooks/useCharges";
import { useChargeUpdater } from "../hooks/useChargeUpdater";
import ChargesCatalogTabs from "./ChargesCatalogTabs";
import ChargesCategoryCard from "./ChargesCategoryCard";
import ChargesEditorModal from "./ChargesEditorModal";
import ChargesVersionHistory from "./ChargesVersionHistory";
import Sidebar from "../../../../DesignSystem/Sidebar";
import { Helmet } from "react-helmet-async";
import "../../../../../css/Finance/Charges/ChargesPage.css";
import Breadcrumb from "../../../../DesignSystem/Breadcrumb/Breadcrumb";

const Toolbar = ({
  catalogName,
  saving,
  onRefresh,
  currentNotes,
  onNotesChange,
  onPublish,
}) => {
  return (
    <div className="charges-header-card">
      <div className="charges-toolbar">
        <div>
          <h2 className="charges-toolbar-title">Paraplanning Charges</h2>
          <div className="charges-toolbar-catalog">{catalogName}</div>
        </div>

        <div className="charges-toolbar-btn-row">
          <button
            type="button"
            className="charges-toolbar-btn"
            onClick={onRefresh}
          >
            Refresh
          </button>

          <button
            type="button"
            className="charges-toolbar-btn-primary"
            disabled={saving}
            onClick={onPublish}
          >
            {saving ? "Saving..." : "Publish"}
          </button>
        </div>
      </div>

      <div style={{ marginTop: 16 }}>
        <label className="charges-toolbar-notes-label">Notes</label>
        <textarea
          className="charges-toolbar-notes"
          value={currentNotes}
          onChange={(e) => onNotesChange(e.target.value)}
          placeholder="Add any notes related to this version here..."
        />
      </div>
    </div>
  );
};

const ChargesPageInner = () => {
  const user = auth?.currentUser || null;
  const { dispatch } = useChargesContext();

  const {
    selectedCatalogId,
    catalogs,
    versionBatches,
    notesByCatalog,
    loading,
    saving,
    editorOpen,
    currentVersionId,
    editorMode,
    selectedItem,
    selectedCategoryId,
    error,
    refreshCatalogs,
  } = useCharges(user);

  const { saveItem, removeItem, publishAllVersions, actionError } =
    useChargeUpdater(user);


  const catalogList = useMemo(() => Object.values(catalogs || {}), [catalogs]);

  const currentCatalog = catalogs?.[selectedCatalogId];

  const currentCategoryName =
    currentCatalog?.categories?.find((cat) => cat.id === selectedCategoryId)
      ?.name || "";

  const handleAdd = (categoryId) => {
    dispatch({
      type: "OPEN_EDITOR",
      payload: {
        mode: "create",
        categoryId,
        item: null,
      },
    });
  };

  const handleEdit = (categoryId, item) => {
    dispatch({
      type: "OPEN_EDITOR",
      payload: {
        mode: "edit",
        categoryId,
        item,
      },
    });
  };

  const handleDelete = async (categoryId, item) => {
    const confirmed = window.confirm(`Delete "${item.name}"?`);
    if (!confirmed) return;

    await removeItem({
      catalogId: selectedCatalogId,
      categoryId,
      itemId: item.id,
    });
  };

 const handleNotesChange = (value) => {
   dispatch({
     type: "UPDATE_NOTE_FOR_CATALOG",
     payload: {
       catalogId: selectedCatalogId,
       value,
     },
   });
  };
  
  const handlePublishAll = async () => {
    await publishAllVersions({
      notesByCatalog,
    });
  };

  return (
    <div className="main-content">
      <Helmet>
        <title>Paraplanning Charges</title>
      </Helmet>

      <Sidebar />

      <div className="Main-Container">
        <div className="charges-page">
          <Breadcrumb
            items={[
              {
                label: "Finance",
                to: "/Department/Finance",
              },
              { label: "Paraplanning Charges" },
            ]}
          />
          <Toolbar
            catalogName={currentCatalog?.name || ""}
            saving={saving}
            onRefresh={refreshCatalogs}
            currentNotes={notesByCatalog[selectedCatalogId] || ""}
            onNotesChange={handleNotesChange}
            onPublish={handlePublishAll}
          />

          <ChargesCatalogTabs
            catalogs={catalogList}
            selectedCatalogId={selectedCatalogId}
            onSelect={(catalogId) =>
              dispatch({ type: "SET_SELECTED_CATALOG", payload: catalogId })
            }
          />

          {/* {(error || actionError) && (
            <div
              style={{
                background: "#fef3f2",
                color: "#b42318",
                border: "1px solid #fecdca",
                borderRadius: 12,
                padding: 12,
                marginBottom: 16,
              }}
            >
              {error || actionError}
            </div>
          )} */}

          {loading ? (
            <div style={{ color: "#667085" }}>Loading charges...</div>
          ) : (
            <>
              <div className="charges-card-grid">
                {(currentCatalog?.categories || []).map((category) => (
                  <ChargesCategoryCard
                    key={category.id}
                    category={category}
                    onAddItem={handleAdd}
                    onEditItem={handleEdit}
                    onDeleteItem={handleDelete}
                  />
                ))}
              </div>

              <ChargesVersionHistory
                versionBatches={versionBatches || []}
                currentVersionId={currentVersionId}
              />
            </>
          )}

          <ChargesEditorModal
            open={editorOpen}
            mode={editorMode}
            item={selectedItem}
            categoryName={currentCategoryName}
            saving={saving}
            onClose={() => dispatch({ type: "CLOSE_EDITOR" })}
            onSave={(item) =>
              saveItem({
                catalogId: selectedCatalogId,
                categoryId: selectedCategoryId,
                item,
              })
            }
          />
        </div>
      </div>
    </div>
  );
};

const ChargesPage = () => {
  return (
    <ChargesProvider>
      <ChargesPageInner />
    </ChargesProvider>
  );
};

export default ChargesPage;
